import { differenceInCalendarDays, parseISO } from 'date-fns';
import { RESOURCES } from './groups';
import { createRandom, dayFromToday, TODAY } from './seed';

// 더미 항목·멤버 — 모든 패턴 페이지가 같은 데이터를 다른 방식으로 보여 준다.

/** 항목 상태 — 흐름 순서대로. 정렬도 이 순서를 따른다. */
export const RECORD_STATUSES = [
  '검토 대기',
  '예정',
  '진행 중',
  '종료 임박',
  '완료',
  '취소',
] as const;
export type RecordStatus = (typeof RECORD_STATUSES)[number];

export const RECORD_TYPES = ['유형 A', '유형 B'] as const;
export type RecordType = (typeof RECORD_TYPES)[number];

/** 상태 색은 의미가 고정된다 — 리스트·캘린더·대시보드가 같은 톤을 쓴다. */
export const STATUS_TONE: Record<
  RecordStatus,
  'info' | 'success' | 'warning' | 'neutral' | 'danger'
> = {
  '검토 대기': 'warning',
  예정: 'info',
  '진행 중': 'success',
  '종료 임박': 'warning',
  완료: 'neutral',
  취소: 'danger',
};

export interface DataRecord {
  id: string;
  title: string;
  groupId: string;
  categoryId: string;
  resourceId: string;
  type: RecordType;
  status: RecordStatus;
  /** 1항목 : N멤버 — 최대 2명 */
  memberIds: string[];
  startDate: string;
  endDate: string;
  amount: number;
  quantity: number;
}

export interface Member {
  id: string;
  name: string;
  email: string;
  phone: string;
}

const FAMILY = ['김', '이', '박', '최', '정', '강', '조', '윤', '장', '임', '한', '오', '서', '신'];
const GIVEN = [
  '민준',
  '서연',
  '지호',
  '수아',
  '도윤',
  '지우',
  '현우',
  '소희',
  '세현',
  '아름',
  '하준',
  '예린',
  '시우',
  '유나',
  '건우',
  '채원',
  '은우',
  '다인',
  '태윤',
  '지안',
];

const random = createRandom(20261013);

export const MEMBERS: Member[] = [];
export const RECORDS: DataRecord[] = [];

function createMember(): Member {
  const index = MEMBERS.length;
  const member: Member = {
    id: `M-${String(1001 + index)}`,
    name: `${random.pick(FAMILY)}${random.pick(GIVEN)}`,
    email: `member${String(index + 1).padStart(3, '0')}@example.com`,
    phone: `010-${String(random.int(2000, 9999))}-${String(random.int(1000, 9999))}`,
  };
  MEMBERS.push(member);
  return member;
}

function statusFor(startOffset: number, endOffset: number): RecordStatus {
  if (endOffset < 0) {
    return random.chance(0.15) ? '취소' : '완료';
  }
  if (startOffset > 0) {
    return startOffset > 21 ? '검토 대기' : '예정';
  }
  return endOffset <= 30 ? '종료 임박' : '진행 중';
}

// 리소스마다 과거 → 현재 → 미래 항목을 겹치지 않게 이어 붙인다.
for (const resource of RESOURCES) {
  let cursor = -random.int(380, 460);
  // 미래 항목은 두 달 안쪽까지만 — 리스트 첫 화면이 먼 미래로 채워지지 않게 한다.
  while (cursor < 60) {
    const type: RecordType = random.chance(0.3) ? '유형 B' : '유형 A';
    const length = type === '유형 B' ? random.int(30, 90) : random.int(180, 365);
    const memberIds = [createMember().id];
    if (random.chance(0.25)) {
      memberIds.push(createMember().id);
    }
    RECORDS.push({
      id: `R-${String(2401 + RECORDS.length)}`,
      title: `샘플 항목 ${String(RECORDS.length + 1).padStart(3, '0')}`,
      groupId: resource.groupId,
      categoryId: resource.categoryId,
      resourceId: resource.id,
      type,
      status: statusFor(cursor, cursor + length),
      memberIds,
      startDate: dayFromToday(cursor),
      endDate: dayFromToday(cursor + length),
      amount: random.int(50, 250) * 10_000,
      quantity: random.int(1, 20),
    });
    cursor += length + random.int(2, 45);
  }
}

RECORDS.sort((a, b) => b.startDate.localeCompare(a.startDate));

const recordById = new Map(RECORDS.map((record) => [record.id, record]));
const memberById = new Map(MEMBERS.map((member) => [member.id, member]));

export const getRecord = (id: string) => recordById.get(id);
export const getMember = (id: string) => memberById.get(id);

export function daysFromToday(date: string) {
  return differenceInCalendarDays(parseISO(date), TODAY);
}
